import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useSearchProducts } from "@babackapp/private-api-client/client/use-search-products";
import useSimulateSize, {
  type SimulateSizeBody,
  type SimulateSizeResult,
} from "@babackapp/private-api-client/client/use-simulate-size";
import useSizingSignal from "@babackapp/private-api-client/client/use-sizing-signal";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { ChevronsUpDown, Loader2 } from "lucide-react";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

const NONE = "none" as const;

export function SizingSimulatorCard() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const mutation = useSimulateSize(shop.id);

  const [productId, setProductId] = useState("");
  const [gender, setGender] = useState<"male" | "female" | typeof NONE>(NONE);
  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [fitPreference, setFitPreference] = useState<
    "slim" | "regular" | "loose" | typeof NONE
  >(NONE);
  const [age, setAge] = useState("");
  const [result, setResult] = useState<SimulateSizeResult | null>(null);

  const canSubmit =
    productId.trim().length > 0 &&
    gender !== NONE &&
    heightCm !== "" &&
    weightKg !== "";

  function handleSubmit() {
    if (!canSubmit) return;

    const body: SimulateSizeBody = {
      productId: productId.trim(),
      gender,
      heightCm: parseFloat(heightCm),
      weightKg: parseFloat(weightKg),
      ...(fitPreference !== NONE && { fitPreference }),
      ...(age !== "" && { age: parseInt(age, 10) }),
    };

    mutation.mutate(body, {
      onSuccess: (data) => setResult(data),
      onError: () => setResult(null),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("sizing.simulator.title")}</CardTitle>
        <CardDescription>{t("sizing.simulator.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2 space-y-2">
            <Label>{t("sizing.simulator.productId")}</Label>
            <ProductCombobox
              shopId={shop.id}
              value={productId}
              onChange={setProductId}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sim-gender">{t("sizing.simulator.gender")}</Label>
            <Select
              value={gender}
              onValueChange={(v) => setGender(v as "male" | "female")}
            >
              <SelectTrigger id="sim-gender">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">
                  {t("sizing.simulator.genderMale")}
                </SelectItem>
                <SelectItem value="female">
                  {t("sizing.simulator.genderFemale")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sim-fit">
              {t("sizing.simulator.fitPreference")}
            </Label>
            <Select
              value={fitPreference}
              onValueChange={(v) =>
                setFitPreference(
                  v as "slim" | "regular" | "loose" | typeof NONE,
                )
              }
            >
              <SelectTrigger id="sim-fit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                <SelectItem value="slim">
                  {t("sizing.simulator.fitSlim")}
                </SelectItem>
                <SelectItem value="regular">
                  {t("sizing.simulator.fitRegular")}
                </SelectItem>
                <SelectItem value="loose">
                  {t("sizing.simulator.fitLoose")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sim-height">{t("sizing.simulator.heightCm")}</Label>
            <Input
              id="sim-height"
              type="number"
              min={100}
              max={230}
              value={heightCm}
              onChange={(e) => setHeightCm(e.target.value)}
              onBlur={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v))
                  setHeightCm(String(Math.min(230, Math.max(100, v))));
              }}
              placeholder="170"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sim-weight">{t("sizing.simulator.weightKg")}</Label>
            <Input
              id="sim-weight"
              type="number"
              min={30}
              max={200}
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              onBlur={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v))
                  setWeightKg(String(Math.min(200, Math.max(30, v))));
              }}
              placeholder="70"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sim-age">{t("sizing.simulator.age")}</Label>
            <Input
              id="sim-age"
              type="number"
              min={1}
              max={120}
              value={age}
              onChange={(e) => setAge(e.target.value)}
              onBlur={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v)) setAge(String(Math.min(120, Math.max(1, v))));
              }}
              placeholder="30"
            />
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || mutation.isPending}
        >
          {mutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {t("sizing.simulator.simulate")}
        </Button>

        {result && <SimulatorResult result={result} />}

        {productId.trim().length > 0 && (
          <div className="max-w-xs">
            <FitGuidePreview shopId={shop.id} productId={productId.trim()} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductCombobox({
  shopId,
  value,
  onChange,
}: {
  shopId: string;
  value: string;
  onChange: (id: string) => void;
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const debouncedSetQ = useDebouncedCallback(setDebouncedQ, 250);

  const {
    data: products,
    isLoading,
    error: searchFailure,
  } = useSearchProducts(shopId, debouncedQ);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedLabel || t("sizing.simulator.productIdPlaceholder")}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={inputValue}
            onValueChange={(v) => {
              setInputValue(v);
              debouncedSetQ(v);
            }}
            placeholder={t("sizing.simulator.productIdPlaceholder")}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading && t("misc.loading")}
              {/* The list now comes from Shopify, which can answer 502. Showing
                  "no result" for an outage is the same silent ambiguity this
                  selector was fixed for (BBS-328). */}
              {!isLoading && searchFailure && (
                <span className="text-destructive">
                  {t("productSelect.searchError")}
                </span>
              )}
              {!isLoading && !searchFailure && t("productSelect.noResult")}
            </CommandEmpty>
            <CommandGroup>
              {(searchFailure ? [] : (products ?? [])).map((product) => (
                <CommandItem
                  key={product.id}
                  value={product.id}
                  onSelect={() => {
                    onChange(product.id);
                    setSelectedLabel(product.title);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{product.title}</span>
                    {product.sku && (
                      <span className="text-xs text-muted-foreground truncate">
                        {product.sku}
                      </span>
                    )}
                  </span>
                  {/* Archived and draft products are listed rather than hidden,
                      so they carry their status here too. */}
                  {product.status !== "ACTIVE" && (
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {product.status === "ARCHIVED"
                        ? t("productSelect.statusArchived")
                        : t("productSelect.statusDraft")}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type FitKey = "tight" | "ideal" | "loose";

function SimulatorResult({ result }: { result: SimulateSizeResult }) {
  const t = useTranslation();
  const [selectedFit, setSelectedFit] = useState<FitKey>("ideal");

  const sizeByFit: Record<FitKey, string | undefined> = {
    tight: result.allSizes.find((s) => s.fit === "tight")?.label,
    ideal: result.allSizes.find((s) => s.fit === "ideal")?.label,
    loose: result.allSizes.find((s) => s.fit === "loose")?.label,
  };

  const displayedSize = sizeByFit[selectedFit] ?? result.recommendedSize;

  return (
    <div className="rounded-lg border bg-muted/30 p-6 space-y-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {t("sizing.simulator.recommendedSize")}
      </p>

      <div className="text-center space-y-1">
        <p className="text-7xl font-bold leading-none">{displayedSize}</p>
        <p className="text-sm font-medium text-green-600">
          {t(`sizing.simulator.fitLabel.${selectedFit}`)}
        </p>
      </div>

      <div className="flex text-xs font-semibold uppercase tracking-wider">
        {(["tight", "ideal", "loose"] as FitKey[]).map((fit) => (
          <button
            key={fit}
            type="button"
            disabled={!sizeByFit[fit]}
            onClick={() => setSelectedFit(fit)}
            className={`flex-1 py-2.5 border transition-colors first:rounded-l-lg last:rounded-r-lg -mr-px last:mr-0 ${
              selectedFit === fit
                ? "border-green-600 text-green-600 z-10 relative"
                : "border-border text-muted-foreground hover:text-foreground disabled:opacity-30"
            }`}
          >
            {t(`sizing.simulator.fitLabel.${fit}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

function FitGuidePreview({
  shopId,
  productId,
}: {
  shopId: string;
  productId: string;
}) {
  const t = useTranslation();
  const { data, isLoading } = useSizingSignal(shopId, productId);

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("sizing.simulator.fitGuide.title")}
      </div>
    );
  }

  if (!data || data.sampleSize < 10) {
    return (
      <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
        <p className="font-medium mb-1">
          {t("sizing.simulator.fitGuide.title")}
        </p>
        <p>{t("sizing.simulator.fitGuide.noData")}</p>
      </div>
    );
  }

  const pct = Math.max(0, Math.min(100, Math.round(data.balance * 100)));

  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-medium">
          {t("sizing.simulator.fitGuide.title")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("sizing.simulator.fitGuide.sampleSize").replace(
            "{{count}}",
            data.sampleSize.toLocaleString(),
          )}
        </p>
      </div>
      <div className="grid grid-cols-3 text-xs text-muted-foreground">
        <span>{t("sizing.simulator.fitGuide.fitted")}</span>
        <span className="text-center">
          {t("sizing.simulator.fitGuide.relaxed")}
        </span>
        <span className="text-right">
          {t("sizing.simulator.fitGuide.oversized")}
        </span>
      </div>
      <svg
        style={{ display: "block", width: "100%", overflow: "visible" }}
        height="14"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <line
          x1="0%"
          y1="7"
          x2="100%"
          y2="7"
          stroke="currentColor"
          strokeWidth="1"
        />
        {[0, 25, 50, 75, 100].map((x) => (
          <line
            key={x}
            x1={`${x}%`}
            y1="3.5"
            x2={`${x}%`}
            y2="10.5"
            stroke="currentColor"
            strokeWidth="1"
          />
        ))}
        <circle cx={`${pct}%`} cy="7" r="5" fill="currentColor" />
      </svg>
    </div>
  );
}
